import React, {
  useMemo,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import { fetchWithAuth, fetchWithAuthV2 } from "./utils";
import "./AbsenceModal.css";

/* ================================
   TYPES
================================ */

interface Props {
  isOpen: boolean;
  start: string;
  end: string;
  manningInfoId: string;
  onClose: () => void;
  onSave: (manningInfoId: string,payload: AbsenceSavePayload[]) => Promise<void>;
}

export interface AbsenceModalHandle {
  loadUnavailability: (manningInfoId: string) => Promise<void>;
}

export interface AbsenceSavePayload {
  weekdate: string;
  days: string; // "" means cleared
}

/* ================================
   DATE HELPERS (UTC SAFE)
================================ */

const parseIsoUTC = (iso: string) => new Date(`${iso}T00:00:00Z`);
const toIso = (d: Date) => d.toISOString().slice(0, 10);


/* ================================
   COMPONENT
================================ */

const AbsenceModal = forwardRef<AbsenceModalHandle, Props>(
  ({ isOpen, start, end, onClose, onSave }, ref) => {
    /* ================================
       STATE
    ================================ */

    const startDate = useMemo(() => parseIsoUTC(start), [start]);

    const endDate = useMemo(() => {
      const d = parseIsoUTC(end);
      d.setUTCDate(d.getUTCDate() + 6); // allow full week span
      return d;
    }, [end]);

    const [selectedDates, setSelectedDates] = useState<string[]>([]);
    const [viewYear, setViewYear] = useState(startDate.getUTCFullYear());
    const [viewMonth, setViewMonth] = useState(startDate.getUTCMonth());
    const [originalWeekMap, setOriginalWeekMap] = useState<Record<string, string[]>>({});
    const [isSaving, setIsSaving] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);

    const manningIdRef = React.useRef<string>("");

    /* ================================
       RESET ON OPEN
    ================================ */

    useEffect(() => {
      if (!isOpen) return;
      setViewYear(startDate.getUTCFullYear());
      setViewMonth(startDate.getUTCMonth());
    }, [isOpen, startDate]);

    /* ================================
       API FETCH (EXPOSED)
    ================================ */

const expandDaysToIso = (weekdate: string, days: string[]) => {
  const weekStart = new Date(`${weekdate}T00:00:00Z`); // Monday

  return days.map((mmdd) => {
    const [mm, dd] = mmdd.split("-").map(Number);

    // Start from weekStart and search forward until MM-DD matches
    const d = new Date(weekStart);

    for (let i = 0; i < 7; i++) {
      const candidateMonth = d.getUTCMonth() + 1; // 1–12
      const candidateDay = d.getUTCDate();         // 1–31

      if (candidateMonth === mm && candidateDay === dd) {
        return d.toISOString().slice(0, 10);
      }

      d.setUTCDate(d.getUTCDate() + 1);
    }

    // If not found, ignore (defensive)
    return null;
  }).filter(Boolean) as string[];
};



      const loadUnavailability = async (id: string) => {
        if (!id) return;

        //  Freeze the ID for the lifetime of this modal session
        manningIdRef.current = id;

        try {
          const url =
            "https://manningserviceapp.azurewebsites.net/api/MainRoute" +
            "?code=GUgZKUh3at7CoJZqYItK4T3zUXK_aJred4q-7EIwAkGMAzFuYSmcaQ==" +
            "&action=get_manning_unavailability" +
            `&manning_id=${encodeURIComponent(id)}`;

          const data = await fetchWithAuth(url);

          const preselected: string[] = [];
          const weekMap: Record<string, string[]> = {};

          (data?.records || []).forEach((r: any) => {
            if (!r.weekdate || !r.days) return;

            const days = r.days.split(",");
            const expanded = expandDaysToIso(r.weekdate, days);

            weekMap[r.weekdate] = expanded;
            expanded.forEach((iso) => {
              if (!preselected.includes(iso)) {
                preselected.push(iso);
              }
            });
          });

          setSelectedDates(preselected);
          setOriginalWeekMap(weekMap);

          // Jump calendar to first absence
          const first = preselected[0];
          if (first) {
            const d = new Date(`${first}T00:00:00Z`);
            setViewYear(d.getUTCFullYear());
            setViewMonth(d.getUTCMonth());
          }
        } catch (err) {
          console.error("Failed to load unavailability", err);
        }
      };


    useImperativeHandle(ref, () => ({
      loadUnavailability,
    }));

    /* ================================
       CALENDAR LOGIC
    ================================ */

    const toggle = (iso: string) => {
      setSelectedDates(prev =>
        prev.includes(iso)
          ? prev.filter(d => d !== iso)
          : [...prev, iso]
      );
    };

    const getMondayWeekdate = (isoDate: string): string => {
      const d = new Date(`${isoDate}T00:00:00Z`);
      const day = d.getUTCDay(); // 0=Sun
      const diff = (day === 0 ? -6 : 1) - day;
      d.setUTCDate(d.getUTCDate() + diff);
      return d.toISOString().slice(0, 10);
    };


    const monthLabel = useMemo(() => {
      const d = new Date(Date.UTC(viewYear, viewMonth, 1));
      return d.toLocaleString("en-US", {
        month: "long",
        year: "numeric",
        timeZone: "UTC",
      });
    }, [viewYear, viewMonth]);

    const gridCells = useMemo(() => {
      const first = new Date(Date.UTC(viewYear, viewMonth, 1));
      const last = new Date(Date.UTC(viewYear, viewMonth + 1, 0));

      const startDow = first.getUTCDay();
      const gridStart = new Date(first);
      gridStart.setUTCDate(gridStart.getUTCDate() - startDow);

      const endDow = last.getUTCDay();
      const gridEnd = new Date(last);
      gridEnd.setUTCDate(gridEnd.getUTCDate() + (6 - endDow));

      const cells = [];
      const d = new Date(gridStart);

      while (d <= gridEnd) {
        const iso = toIso(d);
        const isWeekend = d.getUTCDay() === 0 || d.getUTCDay() === 6;
        const outOfRange = d < startDate || d > endDate;

        cells.push({
          iso,
          dayNum: d.getUTCDate(),
          isThisMonth: d.getUTCMonth() === viewMonth,
          disabled: isWeekend || outOfRange,
        });

        d.setUTCDate(d.getUTCDate() + 1);
      }

      return cells;
    }, [viewYear, viewMonth, startDate, endDate]);

    const goPrevMonth = () => {
      const d = new Date(Date.UTC(viewYear, viewMonth - 1, 1));
      setViewYear(d.getUTCFullYear());
      setViewMonth(d.getUTCMonth());
    };

    const goNextMonth = () => {
      const d = new Date(Date.UTC(viewYear, viewMonth + 1, 1));
      setViewYear(d.getUTCFullYear());
      setViewMonth(d.getUTCMonth());
    };

const computeMondaysBetweenUTC = (startIso: string, endIso: string): string[] => {
  const result: string[] = [];
  let d = new Date(`${startIso}T00:00:00Z`);
  const end = new Date(`${endIso}T00:00:00Z`);

  // snap to Monday
  const day = d.getUTCDay(); // 0..6
  const diff = (day === 0 ? -6 : 1) - day;
  d.setUTCDate(d.getUTCDate() + diff);

  while (d <= end) {
    result.push(d.toISOString().slice(0, 10));
    d.setUTCDate(d.getUTCDate() + 7);
  }

  return result;
};


const handleSaveClick = async () => {
  if (isSaving) return;

  setIsSaving(true);
  setSaveSuccess(false);

  // 1) Build currentWeekMap from selectedDates
  const currentWeekMap: Record<string, string[]> = {};
  selectedDates.forEach((iso) => {
    const monday = getMondayWeekdate(iso);
    if (!currentWeekMap[monday]) currentWeekMap[monday] = [];
    currentWeekMap[monday].push(iso);
  });

  // 2) Union of original weeks + current weeks
  let allWeeks = new Set<string>([
    ...Object.keys(originalWeekMap),
    ...Object.keys(currentWeekMap),
  ]);

  // ✅ 3) FALLBACK: if empty, use all Mondays in the modal range
  // start/end props are the true forecast range boundaries you want.
  if (allWeeks.size === 0) {
    const weeksInRange = computeMondaysBetweenUTC(start, end);
    weeksInRange.forEach((w) => allWeeks.add(w));
  }

  // 4) Build payload (00-00 when no days selected for that week)
  const payload: AbsenceSavePayload[] = Array.from(allWeeks).map((weekdate) => {
    const days = currentWeekMap[weekdate] ?? [];
    return {
      weekdate,
      days: days.length === 0 ? "00-00" : days.map((d) => d.slice(5)).join(","),
    };
  });

  try {
    await onSave(manningIdRef.current, payload);
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 3000);
  } catch (err) {
    console.error("Failed to save absences", err);
    alert("Failed to save absences. Please try again.");
  } finally {
    setIsSaving(false);
  }
};



    /* ================================
       RENDER
    ================================ */

    if (!isOpen) return null;

    return (
      <div className="modal-overlay">
        <div className="modal absence-modal">
          <div className="absence-header">
            <h3>Select Absence Days</h3>

            <div className="absence-month-nav">
              <button className="month-btn" onClick={goPrevMonth}>
                ‹
              </button>
              <div className="month-label">{monthLabel}</div>
              <button className="month-btn" onClick={goNextMonth}>
                ›
              </button>
            </div>
          </div>

          <div className="absence-calendar">
            <div className="dow-row">
              {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
                <div key={d} className="dow">
                  {d}
                </div>
              ))}
            </div>

            <div className="day-grid">
              {gridCells.map((c) => (
                <button
                  key={c.iso}
                  className={[
                    "day",
                    c.isThisMonth ? "" : "muted",
                    c.disabled ? "disabled" : "",
                    selectedDates.includes(c.iso) ? "unavailable" : "",
                  ].join(" ")}
                  disabled={c.disabled}
                  onClick={() => toggle(c.iso)}
                >
                  <span className="day-num">{c.dayNum}</span>
                </button>
              ))}
            </div>
          </div>
          {saveSuccess && (
            <div className="absence-success-msg">
              ✅ Selected days have been saved as unavailable
            </div>
          )}

          <div className="modal-actions">
            <button
              className="btn primary"
              onClick={handleSaveClick}
              disabled={isSaving}
            >
              {isSaving ? "Saving..." : "Save Absence"}
            </button>

            <button
              className="btn secondary"
              onClick={onClose}
              disabled={isSaving}
            >
              Cancel
            </button>

          </div>
        </div>
      </div>
    );
  }
);

export default AbsenceModal;
